import React, { useCallback, useMemo } from 'react'
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Drawer as MuiDrawer
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { IconContext } from 'react-icons'
import NestedItem from './NestedItem'
import Link from '../navigation/Link'
import { useAuth } from '../../context/AuthContext'
import useAccessControl from '../hooks/useAccessControl'
import HomeIcon from '@mui/icons-material/Home'
import PeopleIcon from '@mui/icons-material/People'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import DescriptionIcon from '@mui/icons-material/Description'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import MoneyOffIcon from '@mui/icons-material/MoneyOff'
import MailIcon from '@mui/icons-material/Mail'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import BusinessIcon from '@mui/icons-material/Business'
import SettingsIcon from '@mui/icons-material/Settings'
import GroupsIcon from '@mui/icons-material/Groups'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import TableViewIcon from '@mui/icons-material/TableView'

const openedMixin = (theme, drawerWidth) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  }
})

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth'
})(({ theme, open, drawerWidth }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme, drawerWidth),
    '& .MuiDrawer-paper': openedMixin(theme, drawerWidth)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}))

const listItemSx = {
  display: 'block',
  my: 0,
  color: 'inherit',
  textDecoration: 'none',
  '&:visited': { color: 'inherit' }
}

const listItemButtonSx = (open) => ({
  minHeight: 48,
  justifyContent: open ? 'initial' : 'center',
  px: 2.5
})

const listItemIconSx = (open) => ({
  minWidth: 0,
  mr: open ? 3 : 'auto',
  justifyContent: 'center'
})

const menuItems = [
  { to: '/', label: 'Accueil', icon: <HomeIcon />, rights: { module: 'Dashboard', is_access: true } },
  'divider',
  { to: '/customers', label: 'Clients', icon: <PeopleIcon />, rights: { module: 'Customers', is_list: true } },
  'divider',
  {
    to: '/interventions',
    label: 'Interventions',
    icon: <AssignmentIcon />,
    rights: { module: 'Interventions', is_list: true }
  },
  { to: '/invoices', label: 'Factures', icon: <ReceiptLongIcon />, rights: { module: 'Invoices', is_list: true } },
  { to: '/creditNotes', label: 'Avoirs', icon: <RequestQuoteIcon />, rights: { module: 'CreditNotes', is_list: true } },
  { to: '/quotes', label: 'Devis', icon: <DescriptionIcon />, rights: { module: 'Quotes', is_list: true } },
  'divider',
  {
    to: '/suppliers',
    label: 'Fournisseurs',
    icon: <LocalShippingIcon />,
    rights: { module: 'Suppliers', is_list: true }
  },
  'divider',
  {
    type: 'nested',
    keyName: 'Accounting',
    label: 'Comptabilité',
    icon: <BusinessCenterIcon />,
    list: [
      {
        keyName: 'AccountingOverview',
        label: 'Comptabilité générale',
        to: '/accounting',
        icon: <AccountBalanceIcon />
      },
      { keyName: 'Income', label: 'Encaissements', to: '/incomes', icon: <AttachMoneyIcon /> },
      { keyName: 'Outcome', label: 'Décaissements', to: '/outcomes', icon: <MoneyOffIcon /> },
      { keyName: 'Tax', label: 'Cotisations URSSAF', to: '/tax', icon: <ReceiptLongIcon /> }
    ],
    rights: { module: 'Income', is_list: true }
  },
  'divider',
  { to: '/emails', label: 'Emails', icon: <MailIcon />, rights: { module: 'Emails', is_access: true } },
  { to: '/calendar', label: 'Calendrier', icon: <CalendarMonthIcon />, rights: { module: 'Events', is_access: true } },
  'divider',
  { to: '/company', label: 'Entreprise', icon: <BusinessIcon />, rights: { module: 'Company', is_view: true } },
  { to: '/settings', label: 'Paramètres', icon: <SettingsIcon />, rights: { module: 'Settings', is_view: true } },
  { to: '/users', label: 'Utilisateurs', icon: <GroupsIcon />, rights: { module: 'Users', is_list: true } },
  { to: '/roles', label: 'Rôles', icon: <AdminPanelSettingsIcon />, rights: { module: 'Roles', is_list: true } },
  { to: '/priceBooks', label: 'Tarifs', icon: <MenuBookIcon />, rights: { module: 'PriceBook', is_list: true } },
  'divider',
  { isAdminOnly: true, to: '/tables', label: 'Tables', icon: <TableViewIcon /> },
  { isAdminOnly: true, to: '/contracts', label: 'Contrats', icon: <AssignmentIcon /> },
  'divider'
]

const cleanMenu = (items, canAccess) => {
  const cleaned = []
  let lastWasDivider = true

  for (const item of items) {
    const isDivider = item === 'divider'
    const isVisible =
      !isDivider && (canAccess(item) || (item.type === 'nested' && item.list.some((sub) => canAccess(sub))))

    if (isVisible) {
      cleaned.push(item)
      lastWasDivider = false
    } else if (isDivider && !lastWasDivider) {
      cleaned.push(item)
      lastWasDivider = true
    }
  }

  if (cleaned.at(-1) === 'divider') cleaned.pop()
  return cleaned
}

const SidebarDrawer = ({ open, drawerWidth = 240 }) => {
  const { user } = useAuth()
  const { hasAccess } = useAccessControl()

  const canAccess = useCallback(
    (item) => (item.rights && hasAccess(item.rights)) || (item.isAdminOnly && user?.is_superAdmin),
    [hasAccess, user]
  )

  const cleanedMenuItems = useMemo(() => cleanMenu(menuItems, canAccess), [canAccess])

  return (
    <Drawer
      variant='permanent'
      open={open}
      drawerWidth={drawerWidth}
      PaperProps={{
        sx: {
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          marginTop: '64px',
          height: `calc(100vh - 64px)`
        }
      }}
    >
      <IconContext.Provider value={{ size: '24px', className: 'top-react-icons' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <List disablePadding>
            {cleanedMenuItems.map((item, index) => {
              if (item === 'divider') {
                return <Divider key={`divider-${index}`} />
              }
              if (item.type === 'nested') {
                return (
                  <NestedItem
                    key={item.keyName}
                    open={open}
                    keyName={item.keyName}
                    label={item.label}
                    icon={item.icon}
                    list={item.list}
                  />
                )
              }
              return (
                <ListItem key={item.to} component={Link} to={item.to} disablePadding sx={listItemSx}>
                  <ListItemButton sx={listItemButtonSx(open)}>
                    <ListItemIcon sx={listItemIconSx(open)}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        </Box>
      </IconContext.Provider>
    </Drawer>
  )
}

export default React.memo(SidebarDrawer)
